import { useMemo, useState } from 'react'
import { BookOpen } from 'lucide-react'
import { Dial, type Detent } from '@/components/Dial'
import { LearnMoreSheet } from '@/components/LearnMoreSheet'
import { Readout } from '@/components/Readout'
import { guideFor } from '@/domain/dialGuide'
import { resolve } from '@/domain/resolve'
import { SCENE_PRESETS } from '@/domain/state'
import {
  EXP_COMP_DETENTS,
  F_NUMBERS,
  ISO_DETENTS,
  SHUTTER_DETENTS,
  SHUTTER_BY_ID,
  formatAperture,
  formatExpComp,
} from '@/domain/dials'
import { DEFAULT_LENS, equivalentFocal, isPrime, maxApertureAt, type Lens } from '@/domain/lens'
import type {
  CameraState,
  ControlId,
  ExposureMode,
  IsoDialId,
  ShutterDialId,
} from '@/domain/types'

const MODES: ExposureMode[] = ['AUTO', 'P', 'S', 'A', 'M']

const MODE_BLURB: Record<ExposureMode, string> = {
  AUTO: 'The camera decides everything. Only the zoom ring still matters.',
  P: 'Camera picks shutter and aperture; you can nudge the pair with the rear dial.',
  S: 'You pick the shutter speed. The camera finds an aperture to match.',
  A: 'You pick the aperture. The camera finds a shutter speed to match. The everyday choice.',
  M: 'You pick everything. The meter just tells you how close you are.',
}

const shutterDetents: Detent<ShutterDialId>[] = SHUTTER_DETENTS.map((d) => ({
  id: d.id,
  engraved: d.engraved,
  red: d.red,
}))

const isoDetents: Detent<IsoDialId>[] = ISO_DETENTS.map((d) => ({
  id: d.id,
  engraved: d.engraved,
}))

// The real dial engraves whole stops and marks the thirds with a dot.
const expDetents: Detent<string>[] = EXP_COMP_DETENTS.map((v) => ({
  id: String(v),
  engraved:
    v === 'C' ? 'C' : Number.isInteger(v) ? (v === 0 ? '0' : v > 0 ? `+${v}` : `−${Math.abs(v)}`) : '·',
}))

const CONTROL_NAMES: Record<ControlId, string> = {
  modeLever: 'Mode selector',
  shutterDial: 'Shutter-speed dial',
  isoDial: 'ISO dial',
  expCompDial: 'Exposure compensation dial',
  mainCommandDial: 'Rear command dial',
  subCommandDial: 'Front command dial',
  photoVideoLever: 'Photo/video selector',
  focalRing: 'Zoom ring',
}

export function Simulator({
  initialState,
  lens = DEFAULT_LENS,
}: {
  initialState: CameraState
  lens?: Lens
}) {
  const [state, setState] = useState<CameraState>(initialState)
  const [selected, setSelected] = useState<ControlId>('modeLever')
  const [sheetOpen, setSheetOpen] = useState(false)
  const resolved = useMemo(() => resolve(state, lens), [state, lens])

  const patch = (p: Partial<CameraState>) => setState((s) => ({ ...s, ...p }))
  const status = resolved.controls[selected]
  const guide = guideFor(selected, lens)

  const apertureChoices = F_NUMBERS.filter(
    (f) => f >= resolved.maxApertureAtFocal && f <= resolved.minApertureAtFocal,
  )

  return (
    <div>
      <p className="mb-4 text-[13px] leading-snug text-muted">
        Turn the dials. Anything that isn't doing anything right now goes grey and explains why —
        that's normal, not a fault.
      </p>

      {/* Mode lever — the control that decides what everything else does */}
      <section className="mb-4">
        <SectionLabel>Mode selector · under the ISO dial</SectionLabel>
        <div
          role="radiogroup"
          aria-label="Exposure mode"
          className="flex gap-1 rounded-xl border border-edge bg-panel p-1"
        >
          {MODES.map((m) => (
            <button
              key={m}
              role="radio"
              aria-checked={state.mode === m}
              onClick={() => {
                patch({ mode: m })
                setSelected('modeLever')
              }}
              className={`flex-1 rounded-lg py-2 text-[13px] font-bold tracking-wide transition-colors ${
                state.mode === m
                  ? 'bg-nikon-red text-white'
                  : 'text-muted hover:bg-edge/60 hover:text-chrome'
              }`}
            >
              {m}
            </button>
          ))}
        </div>
        <p className="mt-2 text-[12px] leading-snug text-muted">{MODE_BLURB[state.mode]}</p>
      </section>

      <Readout resolved={resolved} />

      {/* Top plate */}
      <section className="mt-5">
        <SectionLabel>Top plate</SectionLabel>
        <div className="rounded-2xl border border-edge bg-gradient-to-b from-shell to-panel p-4">
          <div className="flex items-start justify-center gap-3 sm:gap-6">
            <Dial
              label="ISO"
              detents={isoDetents}
              value={state.isoDial}
              onChange={(id) => patch({ isoDial: id })}
              status={resolved.controls.isoDial}
              selected={selected === 'isoDial'}
              onSelect={() => setSelected('isoDial')}
              size={104}
            />

            {/* Viewfinder hump */}
            <div className="mt-3 hidden flex-col items-center sm:flex">
              <div className="h-10 w-16 rounded-t-md bg-gradient-to-b from-plate to-plate-dark" />
              <div className="h-3 w-20 rounded-sm bg-plate-dark" />
              <span className="mt-2 text-[9px] uppercase tracking-[.2em] text-muted">Nikon</span>
            </div>

            <Dial
              label="Shutter"
              detents={shutterDetents}
              value={state.shutterDial}
              onChange={(id) => patch({ shutterDial: id })}
              status={resolved.controls.shutterDial}
              selected={selected === 'shutterDial'}
              onSelect={() => setSelected('shutterDial')}
              size={116}
              centreLabel={SHUTTER_BY_ID.get(state.shutterDial)?.label}
            />

            <Dial
              label="Exp. comp"
              detents={expDetents}
              value={String(state.expCompDial)}
              onChange={(id) => patch({ expCompDial: id === 'C' ? 'C' : Number(id) })}
              status={resolved.controls.expCompDial}
              selected={selected === 'expCompDial'}
              onSelect={() => setSelected('expCompDial')}
              size={92}
              centreLabel={
                state.expCompDial === 'C' ? 'C' : formatExpComp(state.expCompDial as number)
              }
            />
          </div>

          {/* Aperture window — the Z fc has no aperture ring */}
          <button
            onClick={() => setSelected('subCommandDial')}
            className={`mt-4 flex w-full items-center justify-between rounded-xl border px-3 py-2 text-left transition-colors ${
              selected === 'subCommandDial'
                ? 'border-nikon-red bg-panel'
                : 'border-edge bg-ink/40 hover:border-muted/40'
            }`}
          >
            <span className="text-[10px] font-semibold uppercase tracking-[.14em] text-muted">
              Aperture window
            </span>
            <span
              className="rounded bg-plate px-2.5 py-0.5 text-sm font-bold text-engrave tabular-nums"
              style={{ fontFamily: 'var(--font-dial)' }}
            >
              {resolved.aperture.label}
            </span>
          </button>
        </div>
      </section>

      {/* What the selected control is doing */}
      <section className="mt-4">
        <div
          className={`rounded-2xl border p-4 ${
            status.state === 'inert' ? 'border-nikon-red/40 bg-nikon-red/5' : 'border-edge bg-panel'
          }`}
        >
          <div className="flex items-center gap-2">
            <span
              className={`inline-block h-2 w-2 rounded-full ${
                status.state === 'inert' ? 'bg-nikon-red' : 'bg-mint'
              }`}
            />
            <span className="text-[11px] font-semibold uppercase tracking-[.14em]">
              {CONTROL_NAMES[selected]} — {status.state === 'inert' ? 'does nothing' : 'live'}
            </span>
          </div>
          <p className="mt-2 text-[14px] font-medium leading-snug">{status.role}</p>
          {status.reason && (
            <p className="mt-1.5 text-[13px] leading-relaxed text-muted">{status.reason}</p>
          )}
          <p className="mt-2.5 border-t border-edge/60 pt-2.5 text-[13px] leading-relaxed text-muted">
            {guide.blurb}
          </p>
          <button
            onClick={() => setSheetOpen(true)}
            className="mt-2.5 flex items-center gap-1.5 rounded-lg border border-edge px-3 py-1.5 text-[12px] font-medium text-muted transition-colors hover:border-muted/40 hover:text-chrome"
          >
            <BookOpen size={13} aria-hidden="true" />
            Learn more
            {guide.examples ? ' — with sample shots' : ''}
          </button>
        </div>
      </section>

      {sheetOpen && (
        <LearnMoreSheet
          control={selected}
          controlName={CONTROL_NAMES[selected]}
          lens={lens}
          onClose={() => setSheetOpen(false)}
        />
      )}

      {/* Command dials */}
      <section className="mt-4 grid grid-cols-2 gap-3">
        <CommandDialCard
          name="Rear dial"
          id="mainCommandDial"
          selected={selected}
          onSelect={setSelected}
          status={resolved.controls.mainCommandDial}
        />
        <CommandDialCard
          name="Front dial"
          id="subCommandDial"
          selected={selected}
          onSelect={setSelected}
          status={resolved.controls.subCommandDial}
        />
      </section>

      {/* Aperture, only meaningful when the front dial owns it */}
      {(state.mode === 'A' || state.mode === 'M') && (
        <section className="mt-4">
          <SectionLabel>
            Aperture · front dial · {formatAperture(resolved.maxApertureAtFocal)}–
            {formatAperture(resolved.minApertureAtFocal)} at {state.focalLength}mm
          </SectionLabel>
          <div className="flex flex-wrap gap-1.5">
            {apertureChoices.map((f) => (
              <button
                key={f}
                onClick={() => {
                  patch({ subCommandDialAperture: f })
                  setSelected('subCommandDial')
                }}
                className={`rounded-lg px-2.5 py-1.5 text-[12px] font-semibold tabular-nums transition-colors ${
                  Math.abs(resolved.aperture.value - f) < 0.001
                    ? 'bg-chrome text-ink'
                    : 'bg-panel text-muted hover:bg-edge hover:text-chrome'
                }`}
              >
                {formatAperture(f)}
              </button>
            ))}
          </div>
          <p className="mt-2 text-[12px] leading-snug text-muted">
            Your {lens.shortName} lens opens to {formatAperture(resolved.maxApertureAtFocal)}
            {isPrime(lens) ? '.' : ' at this zoom.'}
            {!isPrime(lens) &&
              state.focalLength > lens.minFocal &&
              ` Zoom back towards ${lens.minFocal}mm and it opens up to ${formatAperture(maxApertureAt(lens, lens.minFocal))}.`}
          </p>
        </section>
      )}

      {/* Zoom + light */}
      <section className="mt-5 space-y-4">
        <div>
          <SectionLabel>
            {isPrime(lens)
              ? `Focal length · fixed ${lens.minFocal}mm (${equivalentFocal(lens.minFocal)}mm equivalent)`
              : `Zoom · ${state.focalLength}mm (${equivalentFocal(state.focalLength)}mm equivalent)`}
          </SectionLabel>
          {isPrime(lens) ? (
            <p className="text-[12px] leading-snug text-muted">
              This is a prime lens — no zoom ring. Frame by moving your feet.
            </p>
          ) : (
            <input
              type="range"
              min={lens.minFocal}
              max={lens.maxFocal}
              step={1}
              value={state.focalLength}
              onChange={(e) => {
                patch({ focalLength: Number(e.target.value) })
                setSelected('focalRing')
              }}
              className="w-full accent-[var(--color-nikon-red)]"
              aria-label="Focal length"
            />
          )}
        </div>

        <div>
          <SectionLabel>Light</SectionLabel>
          <div className="flex flex-wrap gap-1.5">
            {SCENE_PRESETS.map((s) => (
              <button
                key={s.id}
                onClick={() => patch({ sceneEv100: s.ev })}
                title={s.hint}
                className={`rounded-lg px-2.5 py-1.5 text-[12px] font-medium transition-colors ${
                  state.sceneEv100 === s.ev
                    ? 'bg-chrome text-ink'
                    : 'bg-panel text-muted hover:bg-edge hover:text-chrome'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Warnings */}
      {resolved.warnings.length > 0 && (
        <section className="mt-5 space-y-2">
          <SectionLabel>What to watch</SectionLabel>
          {resolved.warnings.map((w) => (
            <div
              key={w.title}
              className={`rounded-xl border-l-2 bg-panel/60 px-3 py-2.5 ${
                w.level === 'problem'
                  ? 'border-nikon-red'
                  : w.level === 'caution'
                    ? 'border-amber'
                    : 'border-edge'
              }`}
            >
              <p className="text-[13px] font-semibold">{w.title}</p>
              <p className="mt-0.5 text-[12.5px] leading-relaxed text-muted">{w.detail}</p>
            </div>
          ))}
        </section>
      )}
    </div>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-2 text-[10px] font-semibold uppercase tracking-[.16em] text-muted">
      {children}
    </h2>
  )
}

function CommandDialCard({
  name,
  id,
  selected,
  onSelect,
  status,
}: {
  name: string
  id: ControlId
  selected: ControlId
  onSelect: (id: ControlId) => void
  status: { state: string; role: string }
}) {
  const inert = status.state === 'inert'
  return (
    <button
      onClick={() => onSelect(id)}
      className={`rounded-xl border p-3 text-left transition-colors ${
        selected === id ? 'border-nikon-red bg-panel' : 'border-edge bg-panel/50 hover:border-muted/40'
      } ${inert ? 'opacity-55' : ''}`}
    >
      <div className="flex items-center gap-1.5">
        <span className={`h-1.5 w-1.5 rounded-full ${inert ? 'bg-nikon-red' : 'bg-mint'}`} />
        <span className="text-[10px] font-semibold uppercase tracking-[.14em] text-muted">
          {name}
        </span>
      </div>
      <p className="mt-1 text-[12.5px] font-medium leading-snug">{status.role}</p>
    </button>
  )
}
